import {
  FieldValueView,
  type FieldValueViewProps
} from '@databases/fields/FieldValueView';
import { defaultStoreUtils } from '@databases/redux';
import { DatabaseFieldType, isDatabaseTextValue } from '@databases/types/database';
import {
  getStringRepresentation,
  isEmptyValue
} from '@databases/utils/database/field-types';
import {
  getConvertedDatabaseValue,
  getDatabaseEntryRequired,
  getDatabaseFieldRequired
} from '@databases/utils/database/utils';
import { profileDatabaseView } from '@databases/utils/database/view/render-profiling';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { type FunctionComponent } from 'react';

export interface DatabaseValueViewProps extends Omit<
  FieldValueViewProps,
  'field' | 'value'
> {
  contentId: string;
  fieldId: string;
  entryId: string;
}

/**
 * Renders a database value. The right view component is automatically chosen based on the field type of the value.
 */
const DatabaseValueView: FunctionComponent<DatabaseValueViewProps> = ({
  contentId,
  fieldId,
  entryId,
  showFieldNamePlaceholder,
  emptyValueLabel,
  ...props
}) => {
  const [field, value, shouldShowFieldNamePlaceholder, shouldShowEmptyValueLabel] =
    defaultStoreUtils.useEqualSelector((state) => {
      const databaseDraft = selectDatabaseByContentIdRequired(state, {
        contentId
      });
      const entry = getDatabaseEntryRequired(databaseDraft, entryId);
      const field = getDatabaseFieldRequired(databaseDraft, fieldId);
      const value = getConvertedDatabaseValue(databaseDraft, field, entry, state);
      const isValueEmpty = isEmptyValue(
        { database: databaseDraft, field, entry, value },
        state
      );
      const isBlankTextValue =
        isDatabaseTextValue(value.value) && value.value.plain.trim().length === 0;
      const isExternalReferenceField =
        field.type === DatabaseFieldType.ENTRY_LINK ||
        field.type === DatabaseFieldType.PAGE_LINK ||
        field.type === DatabaseFieldType.JIRA_ISSUE_DETAILS;
      const externalReferenceText =
        isExternalReferenceField &&
        getStringRepresentation(
          { database: databaseDraft, field, entry, value },
          state
        )?.trim();
      const isEmptyExternalReferenceValue =
        isExternalReferenceField && !externalReferenceText;
      const shouldShowEmptyLabelValue =
        isValueEmpty || isBlankTextValue || isEmptyExternalReferenceValue;

      let shouldShowFieldNamePlaceholder = false;

      if (showFieldNamePlaceholder && entryId) {
        shouldShowFieldNamePlaceholder = shouldShowEmptyLabelValue;
      }

      return [
        field,
        value,
        shouldShowFieldNamePlaceholder,
        Boolean(emptyValueLabel) && shouldShowEmptyLabelValue
      ] as const;
    });

  const fieldValueView = (
    <FieldValueView
      {...props}
      contentId={contentId}
      entryId={entryId}
      field={field}
      value={value}
      showFieldNamePlaceholder={shouldShowFieldNamePlaceholder}
      showEmptyValueLabel={shouldShowEmptyValueLabel}
      emptyValueLabel={emptyValueLabel}
    />
  );

  return profileDatabaseView(
    `database-view-profile-field-value-${field.type}`,
    fieldValueView
  );
};

export default React.memo(DatabaseValueView);
